import { GlobalConfig, useVuelidate, Validation } from '@vuelidate/core'
import { DocumentNode } from '@apollo/client/link/core/types'
import {
  InputObjectTypeDefinitionNode,
  InputValueDefinitionNode,
  NameNode,
  TypeNode,
} from 'graphql'
import { required, email, numeric } from '@vuelidate/validators'
import { ComputedRef, computed, Ref, unref } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { textHelpers } from '@/utils/tools'

function buildRulesFromGqlDoc(doc: DocumentNode) {
  if (doc.kind !== 'Document') return {}

  const inputs = doc.definitions.filter(
    (def): def is InputObjectTypeDefinitionNode => def.kind === 'InputObjectTypeDefinition',
  )

  const typeNameMapper = (type: TypeNode, propName: String) => {
    if (type.kind !== 'NamedType') return typeNameMapper(type.type, propName)

    switch (type.name.value) {
      case 'String':
        if (propName.includes('email')) {
          return { email }
        }
        break
      case 'Float':
        return { numeric }
    }
    return {}
  }

  const typeMapper = (type: TypeNode, name: NameNode) => {
    switch (type.kind) {
      case 'NonNullType':
        return { [name.value]: { required, ...typeNameMapper(type, name.value) } }
      case 'NamedType':
        return { [name.value]: { ...typeNameMapper(type, name.value) } }
      case 'ListType':
        return typeMapper(type.type, name)
    }
    return {}
  }

  const rec = ({ kind, name, type }: InputValueDefinitionNode) => {
    if (kind !== 'InputValueDefinition') return {}

    return typeMapper(type, name)
  }

  return inputs
    .map((def) => def.fields?.map(rec).reduce((prev, cur) => ({ ...prev, ...cur }), {}))
    .reduce((prev, cur) => ({ ...prev, ...cur }), {})
}

interface CustomVuelidate extends Record<string, any> {
  v$: Ref<Validation>
  error: ComputedRef<boolean>
  quietErrors: ComputedRef<string[]>
  touchAll: () => void
  resetAll: () => void
  anyDirty: ComputedRef<boolean>
}

/**
 * A custom wrapper around the useVulidate composable. This composable exposes the specific error messages as props on
 * the returned object for each rule as well as the touch and reset function for each rule. It also returns v$ if you
 * need access to validates own object directly. It also exposes a few of validates props directly. These are:
 * [
 *  error // Boolean ref that is true if there is any error
 *  quietErrors // Array of Errors that may exists from the start without touch having been called (useful for disabling buttons)
 *  touchAll // call touch on all rule state inputs
 *  resetAll // resets all validators
 *  anyDirty // if there is any data in any of the state inputs
 * ]
 *
 * Example:
 * const rules = { myProp: { required } }
 *
 * const { myProp, myPropTouch, myPropReset, v$ } = useCustomVuelidate(rules, state)
 *
 * myPropTouch()
 * console.log(myProp.value) // Should contain an error message
 * myPropReset()
 * console.log(myProp.value) // Should no longer contain an error message
 *
 * @param rules {object} Rules object for vulidate or input type document from gql (will generate the rules from that)
 * @param state {object} The input state that needs validated. It has to be a ref of sort like computed or vue ref.
 * @param config {object} The config parameter is a vulidate config parameter that is passed to the composable as usual
 * @return CustomVuelidate
 */
export default function useCustomVuelidate(
  rules: DocumentNode | any,
  state: Ref<any>[] | any,
  config: GlobalConfig | undefined = undefined,
): CustomVuelidate {
  if (rules?.kind === 'Document') {
    // eslint-disable-next-line no-param-reassign
    rules = buildRulesFromGqlDoc(rules)
  }

  const v$ = useVuelidate(rules, state, config)

  const ruleActivationFunctions = Object.keys(rules).reduce(
    (prev, key) => ({
      [`${key}Touch`]: v$.value[key].$touch,
      [`${key}Reset`]: v$.value[key].$reset,
      ...prev,
    }),
    {},
  )

  // This pattern will match errors starting with "Value...", "This value...", or "This field..."
  const pattern = /(?:This field|The\svalue|value)/i

  const ruleErrorMessages = Object.keys(rules).reduce(
    (prev, key) => ({
      [key]: computed(() => {
        if (v$.value[key].$error) {
          return textHelpers.capitalizeFirstLetter(
            $t(v$.value[key].$errors[0].$message.replace(pattern, ':key'), { key }),
          )
        }
        return undefined
      }),
      ...prev,
    }),
    {},
  )

  const anyDirty = computed(() => v$.value.$anyDirty)
  const error = computed(() => v$.value.$error)
  const touchAll = v$.value.$touch
  const resetAll = v$.value.$reset
  const quietErrors = computed(
    () =>
      v$.value.$silentErrors.map<string>((err) =>
        textHelpers.capitalizeFirstLetter(
          $t(unref(err.$message).replace(pattern, ':key'), { key: err.$property }),
        ),
      ) ?? [],
  )

  return {
    v$,
    error,
    quietErrors,
    touchAll,
    resetAll,
    anyDirty,
    ...ruleActivationFunctions,
    ...ruleErrorMessages,
  }
}
